import React, { useEffect, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import {
  effectivePricePerKwh,
  fetchElectricityPricesByGeography,
  ElectricityPriceRow,
} from '../../services/electricityPricesByGeographyService';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

/** Align with electricity consumption charts (2005–2023). */
const MAX_PRICE_CHART_YEAR = 2023;

const SECTOR_COLORS: Record<string, { border: string; bg: string }> = {
  Residential: { border: '#1e40af', bg: 'rgba(30, 64, 175, 0.1)' },
  Commercial: { border: '#059669', bg: 'rgba(5, 150, 105, 0.1)' },
  Industrial: { border: '#b45309', bg: 'rgba(180, 83, 9, 0.1)' },
  Transportation: { border: '#7c3aed', bg: 'rgba(124, 58, 237, 0.1)' }
};

const UTILITY_COLORS: string[] = ['#1e40af', '#059669', '#b45309', '#7c3aed', '#be185d', '#0d9488', '#ca8a04', '#6b7280'];

function buildComparisonChartData(
  locationRows: ElectricityPriceRow[],
  caRows: ElectricityPriceRow[],
  locationName: string,
  showComparison: boolean,
  sectorFilter?: string
) {
  const years = Array.from(new Set([...locationRows.map(r => r.year), ...caRows.map(r => r.year)])).sort((a, b) => a - b);
  const sectors = sectorFilter
    ? [sectorFilter]
    : ['Residential', 'Commercial', 'Industrial', 'Transportation'];
  const datasets: { label: string; data: number[]; borderColor: string; backgroundColor: string; borderWidth: number; borderDash?: number[] }[] = [];
  const cDefault = { border: '#6b7280', bg: 'rgba(107, 114, 128, 0.1)' };

  sectors.forEach(sector => {
    const c = SECTOR_COLORS[sector] || cDefault;
    const locVals = years.map(y => {
      const r = locationRows.find(x => x.year === y && x.sector === sector);
      return r ? effectivePricePerKwh(r) : null;
    });
    if (locVals.some(v => v != null)) {
      datasets.push({
        label: `${locationName} – ${sector}`,
        data: locVals.map(v => v ?? undefined) as number[],
        borderColor: c.border,
        backgroundColor: c.bg,
        borderWidth: 2
      });
    }
    if (showComparison && caRows.some(r => r.sector === sector)) {
      const caVals = years.map(y => {
        const r = caRows.find(x => x.year === y && x.sector === sector);
        return r ? effectivePricePerKwh(r) : null;
      });
      datasets.push({
        label: `California – ${sector}`,
        data: caVals.map(v => (v != null ? v : undefined)) as number[],
        borderColor: c.border,
        backgroundColor: 'transparent',
        borderWidth: 2,
        borderDash: [6, 4]
      });
    }
  });

  return { labels: years, datasets };
}

const ENERGY_COLOR = '#059669';
const DELIVERY_COLOR = '#1e40af';

/** Build stacked or single bar chart: when energy_price_per_kwh + delivery_price_per_kwh exist, stack CCA (Energy) + IOU (Delivery); for years with only avg (pre-CCA), show IOU (Bundled). */
function buildUtilityByYearChartData(
  locationRows: ElectricityPriceRow[],
  sectorFilter: string,
  ccaName: string | null,
  iouName: string | null
) {
  const rows = locationRows.filter(r => r.sector === sectorFilter);
  const byYear = new Map<number, ElectricityPriceRow>();
  for (const r of rows) {
    byYear.set(r.year, r);
  }
  const years = Array.from(byYear.keys()).sort((a, b) => a - b);
  const hasStacked = years.some(y => {
    const r = byYear.get(y);
    return r != null && r.energy_price_per_kwh != null && r.delivery_price_per_kwh != null;
  });
  const hasBundledOnly = years.some(y => {
    const r = byYear.get(y);
    return r != null && r.avg_price_per_kwh != null && (r.energy_price_per_kwh == null || r.delivery_price_per_kwh == null);
  });

  if (hasStacked && (ccaName || iouName)) {
    const energyData = years.map(y => {
      const r = byYear.get(y);
      return r?.energy_price_per_kwh ?? null;
    });
    const deliveryData = years.map(y => {
      const r = byYear.get(y);
      return r?.delivery_price_per_kwh ?? null;
    });
    const datasets: { label: string; data: (number | null)[]; backgroundColor: string; borderColor: string; borderWidth: number; stack?: string }[] = [];
    if (ccaName) {
      datasets.push({ label: `${ccaName} (Energy)`, data: energyData, backgroundColor: ENERGY_COLOR, borderColor: ENERGY_COLOR, borderWidth: 1, stack: 'stack0' });
    }
    if (iouName) {
      datasets.push({ label: `${iouName} (Delivery)`, data: deliveryData, backgroundColor: DELIVERY_COLOR, borderColor: DELIVERY_COLOR, borderWidth: 1, stack: 'stack0' });
    }
    // Pre-CCA years: show previous IOU as bundled bar in same stack so one bar per year (stacked or single), centered on year
    if (hasBundledOnly && iouName) {
      const bundledData = years.map(y => {
        const r = byYear.get(y);
        if (!r || r.avg_price_per_kwh == null) return null;
        if (r.energy_price_per_kwh != null && r.delivery_price_per_kwh != null) return null; // this year uses stacked
        return r.avg_price_per_kwh;
      });
      if (bundledData.some(v => v != null)) {
        datasets.push({
          label: `${iouName} (Bundled)`,
          data: bundledData,
          backgroundColor: '#6b7280',
          borderColor: '#4b5563',
          borderWidth: 1,
          stack: 'stack0'
        });
      }
    }
    return { labels: years, datasets, stacked: true, utilityByYear: Object.fromEntries(years.map(y => [y, byYear.get(y)?.utility_name ?? null])) };
  }

  const utilToColor: Record<string, string> = {};
  const utilities = Array.from(new Set(rows.map(r => r.utility_name).filter(Boolean))) as string[];
  utilities.forEach((u, i) => { utilToColor[u] = UTILITY_COLORS[i % UTILITY_COLORS.length]; });
  const singleData = years.map(y => {
    const r = byYear.get(y);
    return r ? effectivePricePerKwh(r) : null;
  });
  const singleLabel = utilities.length ? (utilities[0] + ' (Bundled)') : 'California average';
  const singleColor = utilities.length ? (utilToColor[utilities[0]] ?? '#6b7280') : '#6b7280';
  const datasets = [{
    label: singleLabel,
    data: singleData,
    backgroundColor: singleColor,
    borderColor: singleColor,
    borderWidth: 1
  }];
  return { labels: years, datasets, stacked: false, utilityByYear: Object.fromEntries(years.map(y => [y, byYear.get(y)?.utility_name ?? null])) };
}

export type ElectricityPricesChartView = 'byUtility' | 'locationVsCA';

interface ElectricityPricesByGeographyChartProps {
  geofips: string | number;
  /** Used for by-name fallback when geofips variants miss price rows. */
  locationName: string;
  chartView?: ElectricityPricesChartView;
  sector?: string;
  showComparisonToCA?: boolean;
  onDataLoaded?: (data: { location: ElectricityPriceRow[]; california: ElectricityPriceRow[]; utilityName: string | null; eia861Iou?: string | null; eia861Cca?: string | null } | null) => void;
}

export const ElectricityPricesByGeographyChart: React.FC<ElectricityPricesByGeographyChartProps> = ({
  geofips,
  locationName,
  chartView = 'locationVsCA',
  sector = 'Residential',
  showComparisonToCA = true,
  onDataLoaded
}) => {
  const [data, setData] = useState<{ location: ElectricityPriceRow[]; california: ElectricityPriceRow[]; utilityName: string | null; eia861Iou?: string | null; eia861Cca?: string | null } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const hasGeofips = geofips !== null && geofips !== undefined && String(geofips).trim() !== '';
    if (!hasGeofips) {
      setData(null);
      setLoading(false);
      setError(null);
      onDataLoaded?.(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await fetchElectricityPricesByGeography(geofips, locationName);
        if (!cancelled) {
          const filtered = {
            ...result,
            location: (result.location || []).filter((r) => r.year <= MAX_PRICE_CHART_YEAR),
            california: (result.california || []).filter((r) => r.year <= MAX_PRICE_CHART_YEAR)
          };
          setData(filtered);
          onDataLoaded?.(filtered);
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load electricity prices');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [geofips, locationName, onDataLoaded]);

  if (loading) return <div className="flex items-center justify-center h-64 text-gray-600">Loading electricity prices…</div>;
  if (error) return <div className="flex items-center justify-center h-64 text-red-600">Error: {error}</div>;
  if (!data || (!data.location.length && !data.california.length)) {
    return <div className="flex items-center justify-center h-64 text-gray-600">No electricity price data for this location.</div>;
  }

  if (chartView === 'byUtility') {
    const barData = buildUtilityByYearChartData(
      data.location,
      sector,
      data.eia861Cca ?? null,
      data.eia861Iou ?? null
    );
    const options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { position: 'top' as const },
        title: { display: true, text: `Electricity price ($/kWh) by year — ${sector}${barData.stacked ? ' (Energy + Delivery)' : ' (utility serving location)'}` },
        tooltip: {
          callbacks: barData.stacked ? undefined : {
            afterBody: (tooltipItems: { dataIndex: number }[]) => {
              const idx = tooltipItems[0]?.dataIndex;
              if (idx == null || !barData.utilityByYear) return [];
              const year = barData.labels[idx] as number;
              const util = (barData.utilityByYear as Record<number, string | null>)[year];
              return util ? [`Utility: ${util}`] : [];
            }
          }
        }
      },
      scales: {
        x: { stacked: barData.stacked, title: { display: true, text: 'Year' } },
        y: { stacked: barData.stacked, title: { display: true, text: barData.stacked ? '$/kWh' : 'Avg $/kWh' }, min: 0 }
      }
    };
    const uniqueUtils = Array.from(new Set(Object.values(barData.utilityByYear || {}).filter(Boolean)));
    return (
      <div>
        {!barData.stacked && uniqueUtils.length > 0 && (
          <p className="text-sm text-gray-600 mb-2">
            Utility: <strong>{uniqueUtils.join(', ')}</strong> (Bundled)
          </p>
        )}
        {barData.stacked && (
          <p className="text-sm text-gray-600 mb-2">
            Stacked: <strong>{data.eia861Cca} (Energy)</strong> + <strong>{data.eia861Iou} (Delivery)</strong>
            {barData.datasets.some(d => (d as { label?: string }).label?.includes('(Bundled)')) && data.eia861Iou && (
              <> — Pre-CCA years: <strong>{data.eia861Iou} (Bundled)</strong></>
            )}
          </p>
        )}
        <div style={{ height: '400px' }}>
          <Bar data={barData} options={options} />
        </div>
      </div>
    );
  }

  const chartData = buildComparisonChartData(data.location, data.california, locationName, showComparisonToCA, sector);
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top' as const },
      title: { display: true, text: `Electricity price ($/kWh) — ${sector}${showComparisonToCA ? ' — location vs California' : ''}` }
    },
    scales: {
      x: { title: { display: true, text: 'Year' } },
      y: { title: { display: true, text: '$/kWh' }, min: 0 }
    }
  };

  return (
    <div>
      {data.utilityName && (
        <p className="text-sm text-gray-600 mb-2">
          Price source: <strong>{data.utilityName}</strong>
          {!data.location.length && ' (location uses California average).'}
        </p>
      )}
      <div style={{ height: '400px' }}>
        <Line data={chartData} options={options} />
      </div>
    </div>
  );
};

export default ElectricityPricesByGeographyChart;
